import { WORKER_STATUSES } from '@hirebotiq-ops/core';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { StatusBadge } from '../components/StatusBadge.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/workers/')({
  component: AdminWorkers,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type StatusFilter = 'all' | (typeof WORKER_STATUSES)[number];

function AdminWorkers() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const list = trpc.workers.list.useQuery({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    limit: 200,
  });

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-bold">Worker</h1>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Input
              placeholder="Suche nach Vor- oder Nachname"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {WORKER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stadt</TableHead>
                  <TableHead>Aufenthalt bis</TableHead>
                  <TableHead>Zuletzt geändert</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Lade …
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Keine Treffer.
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>
                      <Link
                        to="/admin/workers/$id"
                        params={{ id: w.id }}
                        className="hover:underline"
                      >
                        {[w.firstName, w.lastName].filter(Boolean).join(' ') || '(unbenannt)'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={w.status} />
                    </TableCell>
                    <TableCell>{w.addressCity ?? '—'}</TableCell>
                    <TableCell>{w.residencePermitExpiry ?? '—'}</TableCell>
                    <TableCell className="text-xs text-[var(--muted-foreground)]">
                      {new Date(w.updatedAt).toLocaleString('de-DE')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
